import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast';
import { useMS } from 'src/components/files/onedrive/context/MSContext';
import { createFolderInOneDrive, getDriveItems, getOneDriveFiles, getSharingActivities } from 'src/graph';
import { requestScopes } from 'src/authConfig';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { FileIcon, UploadCloudIcon } from 'lucide-react';
import { mimetypeToIcon } from 'src/components/files/onedrive/lib/icons';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { nl } from 'date-fns/locale';
import { Checkbox } from 'src/components/ui/checkbox';
import { RemoveFromDrive } from 'src/components/files/onedrive/components/remove-from-drive';
import { UploadToFitdoc } from 'src/components/files/onedrive/components/upload-to-fitdoc';
import { CONFIG } from 'src/config';

type FRP = {
  selected_documents: string[]
  file:any
  toggleSelect: (id:string) => void
}
const FileRow = ({selected_documents, file, toggleSelect}:FRP) => {
  const navigate = useNavigate()

  function bytesToSize(bytes: number): string {
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      if (bytes === 0) return '0 Byte';
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString());
      return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  }

  function formatTimestamp(timestamp:string) {
      return formatDistanceToNow(parseISO(timestamp), { addSuffix: true, locale:nl });
  }

  function getNavigateURL(file:any) {
      let url = `../${file.id}`
      if(file?.file?.mimeType){
          url = ''
          window.open(file.webUrl)
      }
      return url
  }

  return (
      <TableRow className={`group`}>
        <TableCell>
          <div className={`flex items-center opacity-0 group-hover:opacity-100 ${selected_documents.includes(file.id) && '!opacity-100'}`}><Checkbox onCheckedChange={(v)=>{toggleSelect(file.id)}} checked={selected_documents.includes(file.id)} className={`${!selected_documents.includes(file.id) && 'opacity-0'} group-hover:opacity-100`}/></div>
        </TableCell>
        <TableCell className='whitespace-nowrap max-w-10'>
        <div className='flex items-center text-neutral-500'>
          {mimetypeToIcon(file.file?.mimeType || 'folder')}
        </div>
        </TableCell>
        <TableCell onClick={()=>{navigate(getNavigateURL(file))}} className='whitespace-nowrap cursor-pointer hover:underline'>{file.name}</TableCell>
        <TableCell className='whitespace-nowrap first-letter:uppercase text-neutral-500'>{formatTimestamp(file.lastModifiedDateTime)}</TableCell>
        <TableCell className='whitespace-nowrap text-neutral-500'>{file.lastModifiedBy.user.displayName}</TableCell>
        <TableCell className='text-neutral-500'>{bytesToSize(file.size)}</TableCell>
      </TableRow>
  )
}

const OneDriveEdits = () => {
  const { instance, accounts } = useMsal();
  const { accessToken, fitdocfolder, setfitdocfolder, setaccessToken, setaccount, tmpfolder, settmpfolder } = useMS()

  const [selected_documents, setselected_documents] = useState<string[]>([]);

  function toggleSelect(id:string) {
    setselected_documents(p=>{
        let u = [...p]
        const i = u.indexOf(id)
        if(i<0){ u.push(id) }
        else{ u.splice(i, 1) }
        return u
    })
  }

  async function RequestProfileData() {
    const response = await instance.acquireTokenSilent({...requestScopes, account: accounts[0]})
    setaccount(response.account)
    setaccessToken(response.accessToken)
  }
  useEffect(()=>{
    RequestProfileData()
    if(accessToken && tmpfolder){ getFitdocFiles() }
  }, [])

  const { data:fitdocFiles, refetch:getFitdocFiles } = useQuery({
    queryKey: [`fitdoc-files`],
    queryFn: async () => {
      const data = await getDriveItems(accessToken, tmpfolder.id)
      return data.value
    },
    enabled: !!tmpfolder
  })

  useQuery({
    queryKey: [`onedrive-files`],
    queryFn: async () => {
      const odf = await getOneDriveFiles(accessToken);
      const fitdocFolder = odf.value.find((f:any) => f.name === CONFIG.onedrive.fitdoc_foldername)
      const parentId = odf.value[0].parentReference.id

      if(!fitdocFolder){ 
          toast.loading('FITdoc map aanmaken...', {id:'createFitdocFolder'})
          const folder = await createFolderInOneDrive(accessToken, parentId, CONFIG.onedrive.fitdoc_foldername)
          const tmp = await createFolderInOneDrive(accessToken, folder.id, CONFIG.onedrive.fitdoc_tmp_foldername)
          toast.success('FITdoc map aangemaakt', {id:'createFitdocFolder'})
          setfitdocfolder(folder)
          settmpfolder(tmp)
          return []
      }

      const fdItems = await getDriveItems(accessToken, fitdocFolder.id)
      if(fdItems.value){
          const tmp = fdItems.value.find((f:any)=>f.name === CONFIG.onedrive.fitdoc_tmp_foldername)
          if(!tmp){
              const tmp = await createFolderInOneDrive(accessToken, fitdocFolder.id, CONFIG.onedrive.fitdoc_tmp_foldername)
              settmpfolder(tmp)
          } else { settmpfolder(tmp) }
      }

      setfitdocfolder(fitdocFolder)

      return odf.value
    },
    enabled: !!accessToken
  })

  function allValidDocid () {
    let valid = false;
    if(!Array.isArray(fitdocFiles)){ return valid }
    const docids:string[] = []
    selected_documents.forEach(id => {
        const file = (fitdocFiles?.find((f:any) => f.id === id))
        const docid = file?.name.match(/\[(.*?)\]/) ? (file.name.match(/\[(.*?)\]/)[1] || undefined) : undefined;
        docid && docids.push(docid)
    });
    if(docids.length === selected_documents.length){ valid = true }
    return valid
  }
    
  return (
    <>
    {fitdocFiles?.length > 0 &&
    <div className={`py-3 sticky -top-7 flex items-center gap-1 duration-200`}>
      <RemoveFromDrive selected_documents={selected_documents} onedrivedocuments={fitdocFiles} onSuccess={()=>{setselected_documents([]); getFitdocFiles()}}/>
      {allValidDocid() && <UploadToFitdoc selected_documents={selected_documents} onedrivedocuments={fitdocFiles} onSuccess={()=>{getFitdocFiles()}} trigger={<button disabled={selected_documents.length === 0} className='flex items-center gap-2 text-sm rounded-sm p-2 group hover:bg-neutral-100 disabled:opacity-50'><UploadCloudIcon className='w-4 h-4 group-hover:text-primary' strokeWidth={1.25}/>Uploaden in FitDoc</button>}/>}
    </div>
    }

    <div className='bg-white rounded-md border'>
      <Table>
        <TableHeader><TableRow className={`group ${fitdocFiles?.length === 0 && 'border-none'}`}>
          <TableHead></TableHead>
          <TableHead><FileIcon className='w-4 h-4' strokeWidth={1}/></TableHead>
          <TableHead>Naam</TableHead>
          <TableHead>Gewijzigd</TableHead>
          <TableHead>Gewijzigd door</TableHead>
          <TableHead>Bestandsgrootte</TableHead>
        </TableRow></TableHeader>
        <TableBody>
        {Array.isArray(fitdocFiles) && fitdocFiles.length === 0 && <TableRow><TableCell></TableCell><TableCell colSpan={99}>Geen openstaande documenten</TableCell></TableRow>}

        {Array.isArray(fitdocFiles) && fitdocFiles.map((file, index) => (
          <FileRow selected_documents={selected_documents} toggleSelect={toggleSelect} file={file} key={`file-${index}`}/>
        ))}

        </TableBody>
      </Table>
    </div>
    </>
  )
}

export default OneDriveEdits